import { useTranslation } from "react-i18next"

interface FeatureCardProps {
    title: string
    titleAccent?: string
    description: string
    gifSrc: string
    gifAlt: string
}

function FeatureCard({ title, titleAccent, description, gifSrc, gifAlt }: FeatureCardProps) {
    return (
        <div
            className="feature-card group relative overflow-hidden rounded-lg md:rounded-[32px] bg-bg-surface/60 backdrop-blur-sm border border-white/[0.08] p-lg md:p-xl"
        >
            {/* Shadow background effect */}
            <div className="absolute top-0 -left-1/3 w-full pointer-events-none">
                <img
                    src="/shadow.png"
                    alt=""
                    className="w-full opacity-60 rotate-180 scale-[1.2]"
                />
            </div>

            {/* Card content */}
            <div className="relative z-10 flex flex-col h-full">
                {/* Text content - タイトルが上 */}
                <div className="mb-lg">
                    <h3 className="font-heading-en text-hero text-white mb-sm leading-tight">
                        {title}
                        {titleAccent && (
                            <span className="italic text-brand-accent"> {titleAccent}</span>
                        )}
                    </h3>
                    <p className="font-body-ja text-body leading-relaxed max-w-2xl">
                        {description}
                    </p>
                </div>

                {/* GIF - 自動loop再生 */}
                <div className="relative rounded-md overflow-hidden bg-bg-base/50 aspect-[11/5] -mx-md -mb-md">
                    <img
                        src={gifSrc}
                        alt={gifAlt}
                        className="w-full h-full object-contain"
                    />
                    {/* Subtle gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-bg-base/30 to-transparent pointer-events-none" />
                </div>
            </div>
        </div>
    )
}

// Feature keys for translation lookup
const featureKeys = ['massGeneration', 'yourDesign', 'teamSharing'] as const

const featureAssets: Record<string, { gifSrc: string; gifAlt: string }> = {
    massGeneration: {
        gifSrc: "/feature1.jpg",
        gifAlt: "Mass slide generation in Figma",
    },
    yourDesign: {
        gifSrc: "/feature2.jpg",
        gifAlt: "Use your own design theme",
    },
    teamSharing: {
        gifSrc: "/feature3.jpg",
        gifAlt: "Share templates with your team",
    },
}

export function Features() {
    const { t } = useTranslation('landing')

    return (
        <section className="relative pt-0 pb-xl md:py-3xl overflow-hidden">
            <div className="container relative z-10">
                {/* Feature cards - 縦1列 */}
                <div className="flex flex-col gap-xl max-w-4xl mx-auto">
                    {featureKeys.map((key) => {
                        const assets = featureAssets[key]
                        return (
                            <FeatureCard
                                key={key}
                                title={t(`features.${key}.title`)}
                                description={t(`features.${key}.description`)}
                                gifSrc={assets.gifSrc}
                                gifAlt={assets.gifAlt}
                            />
                        )
                    })}
                </div>
            </div>
        </section>
    )
}
